import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isSuperAdmin } from '@nbh/shared';
import { api, ApiError, type SessionUser } from '../lib/api';
import {
  cacheSessionUser,
  clearCachedAssets,
  clearCachedSessionUser,
  clearUserScopedCaches,
  getCachedSessionUser,
} from '../lib/db';
import { clearApiCache } from '../lib/caches';

interface AuthState {
  user: SessionUser | null;
  /** True until the initial session probe settles, so routes do not flash. */
  loading: boolean;
  /**
   * The app was away long enough that whoever is holding the phone now may not be
   * whoever left it. Rendered by `IdentityCheck`, cleared by `confirmIdentity`.
   */
  identityUnconfirmed: boolean;
  confirmIdentity: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  /**
   * Re-reads the session from the server.
   *
   * Exposed for the password-change form, which has to clear `mustChangePassword` on the
   * client the moment the server clears it — otherwise the router keeps somebody on the
   * forced-change screen they have already finished with, until the next natural probe.
   */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * The only two answers that mean "this session is over" (decision D40).
 *
 * Everything else the server can say — 500 from a crashed handler, 502 while the API
 * container restarts, 503, a captive portal's HTML error page — is a failure to *reach*
 * a verdict, not a verdict. This branch used to be `error instanceof ApiError` with no
 * status check at all, so a deploy restarting the server signed out whoever happened to
 * cold-start during the window, and wiped the cached profile the offline fallback needs
 * on the way past. That damage outlived the blip by design.
 */
const SESSION_REJECTED: ReadonlySet<number> = new Set([401, 403]);

/**
 * How long backgrounded before returning to the app is worth a re-check.
 *
 * Long enough that switching to the camera and back costs nothing; short enough that a
 * phone which sat in a pocket through a shift revalidates before the technician tries to
 * file anything.
 */
const REVALIDATE_AFTER_HIDDEN_MS = 5 * 60 * 1000;

/**
 * How long away before the app should ask who is holding it (DEBT-7).
 *
 * Two hours, not five minutes: this one interrupts, and a prompt that fires when someone
 * comes back from lunch is a prompt people learn to dismiss without reading. Long enough
 * to mean "this phone was put down and picked up again", which on a shared handset is
 * exactly the moment it changes hands.
 */
const CONFIRM_IDENTITY_AFTER_HIDDEN_MS = 2 * 60 * 60 * 1000;

type SessionProbe =
  { kind: 'signed-in'; user: SessionUser } | { kind: 'rejected' } | { kind: 'unreachable' };

/**
 * Asks the server who we are, and classifies the answer into the three cases that lead
 * to different behaviour. Never throws.
 */
async function probeSession(): Promise<SessionProbe> {
  try {
    const fresh = await api.me();
    // Cached on the way past, and deliberately neither awaited nor allowed to fail the
    // probe. Awaiting put a storage write between the server's answer and the app
    // rendering, which on a slow handset is the wrong place for one; catching means a
    // full or unavailable IndexedDB cannot demote a perfectly good session to
    // "unreachable" and drop the user at the login form.
    void cacheSessionUser(fresh).catch(() => undefined);
    return { kind: 'signed-in', user: fresh };
  } catch (error) {
    if (error instanceof ApiError && SESSION_REJECTED.has(error.status)) {
      // The server answered, and the answer was that this session is over. Drop the
      // cached copy too, or the next cold start would resurrect it — awaited here,
      // because the next start must not find it, and caught for the same reason as
      // above but in the more dangerous direction: a throw would turn a real rejection
      // into "unreachable" and leave the app signed in against a dead session.
      await clearCachedSessionUser().catch(() => undefined);
      return { kind: 'rejected' };
    }

    // Either nobody answered — `fetch` rejects with a bare TypeError when there is no
    // network, which is what a plant room actually produces — or something answered
    // that was not the API. Neither is evidence about the session.
    return { kind: 'unreachable' };
  }
}

/**
 * Session state for the app.
 *
 * The token lives in an httpOnly cookie, so nothing here holds it — the session is
 * rehydrated by asking the server who we are. That means a refresh keeps you signed
 * in without the client ever storing a credential it could leak.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [identityUnconfirmed, setIdentityUnconfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      const probe = await probeSession();
      // Only the cold start consults the cache: this is the one moment where there is no
      // current session to leave alone, and rendering the queue from IndexedDB with no
      // signal is the whole point of an offline-first app.
      const remembered = probe.kind === 'unreachable' ? await getCachedSessionUser() : null;
      if (cancelled) return;

      if (probe.kind === 'signed-in') setUser(probe.user);
      else if (probe.kind === 'rejected') setUser(null);
      else setUser(remembered);

      setLoading(false);
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Drops the cached equipment register the moment the session stops being allowed it.
   *
   * `AssetManager` writes the whole register to IndexedDB, and `clearUserScopedCaches`
   * runs only on an explicit sign-out — so an admin who opened the Equipment tab
   * yesterday and is restricted today still had the register on the handset, readable in
   * devtools and served to anything calling `getCachedAssets`.
   *
   * Watching the role rather than hooking each `setUser`: the role is re-read on every
   * `/auth/me`, so a demotion lands here whether it arrives through the cold-start probe,
   * the revalidation after a phone wakes up, or somebody else signing in on a shared
   * handset. One place, and it cannot be forgotten at a fourth call site.
   *
   * A null user clears it too. A rejected probe ends a session without going through
   * sign-out, and the register should not outlive that either.
   */
  useEffect(() => {
    if (isSuperAdmin(user?.role)) return;

    // Best-effort: a handset that cannot open IndexedDB has bigger problems, and failing
    // here must not take the session down with it.
    void clearCachedAssets().catch(() => undefined);
  }, [user?.role]);

  /**
   * Re-checks the session after the app has been away for a while (D40).
   *
   * Unlike the cold start, this leaves an unreachable server alone entirely. There is
   * already a session on screen, and a phone waking up in a dead spot is not a reason
   * to disturb it.
   */
  const revalidate = useCallback(async () => {
    const probe = await probeSession();
    if (probe.kind === 'signed-in') setUser(probe.user);
    else if (probe.kind === 'rejected') setUser(null);
  }, []);

  useEffect(() => {
    // The session used to be probed exactly once, on mount, so a handset backgrounded
    // for a shift never re-checked and only discovered an expired cookie whenever the
    // OS finally killed the app and someone relaunched it. That gap is what made the
    // sign-outs look random.
    let hiddenSince: number | null = null;

    function onVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
      }

      const awayMs = hiddenSince === null ? 0 : Date.now() - hiddenSince;
      hiddenSince = null;
      if (awayMs >= REVALIDATE_AFTER_HIDDEN_MS) void revalidate();
      // Separate threshold, and checked second: a session that turns out to be over
      // signs the user out anyway, and asking "is this you?" of the login screen would
      // be nonsense.
      if (awayMs >= CONFIRM_IDENTITY_AFTER_HIDDEN_MS) setIdentityUnconfirmed(true);
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [revalidate]);

  const confirmIdentity = useCallback(() => setIdentityUnconfirmed(false), []);

  const signIn = useCallback(async (email: string, password: string) => {
    const signedIn = await api.login(email, password);
    setUser(signedIn);
    // Somebody just typed a password. Whoever they are, they are confirmed.
    setIdentityUnconfirmed(false);
    await cacheSessionUser(signedIn);
  }, []);

  const signOut = useCallback(async () => {
    // Clear locally even if the request fails: the user asked to be signed out, and
    // the cookie expires regardless.
    await api.logout().catch(() => undefined);
    setUser(null);
    setIdentityUnconfirmed(false);
    // Cleared unconditionally: leaving it would sign the next cold start back in.
    await clearCachedSessionUser();
    // The service worker's response cache holds attachment bytes and is origin-scoped
    // rather than user-scoped, and the IndexedDB read caches are keyed by filter or row
    // id rather than by user. On a shared handset both would otherwise still be serving
    // the last person's work to whoever signs in next.
    await Promise.all([clearApiCache(), clearUserScopedCaches()]);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      identityUnconfirmed,
      confirmIdentity,
      signIn,
      signOut,
      refresh: revalidate,
    }),
    [user, loading, identityUnconfirmed, confirmIdentity, signIn, signOut, revalidate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
